import {
    type NewPairsStreamParams,
    type NewPairsStreamResponse,
    type OHLCVPairsStreamParams,
    type OHLCVPairsStreamResponse,
    type OHLCVTokensStreamParams,
    type OHLCVTokensStreamResponse,
    type StreamingServiceConfig,
    type StreamSubscriptionOptions,
    type TokenSearchParams,
    type TokenSearchResponse,
    type UnsubscribeFunction,
    type UpdatePairsStreamParams,
    type UpdatePairsStreamResponse,
    type UpdateTokensStreamParams,
    type UpdateTokensStreamResponse,
    type UPnLForTokenParams,
    type UPnLForTokenResponse,
    type UPnLForWalletParams,
    type UPnLForWalletResponse,
    type WalletActivityStreamParams,
    type WalletActivityStreamResponse,
} from "../utils/types/StreamingService.types";
import { createClient, type Client } from "graphql-ws";

/**
 * Streaming API Service
 *
 */
export class StreamingService {
    private defaultConfig: Required<StreamingServiceConfig> = {
        shouldRetry: (retries) => retries < 5,
        maxReconnectAttempts: 5,
        onConnecting: () => {
            console.info("StreamingService Connection Connecting...");
        },
        onOpened: () => {
            console.info(
                "StreamingService Connection Established Successfully!"
            );
        },
        onClosed: () => {
            console.info("StreamingService Connection Closed");
        },
        onError: (err) => {
            console.error("StreamingService Connection Error:", err);
        },
    };

    constructor(apiKey: string, config?: StreamingServiceConfig) {
        StreamingWebSocketClient.configure(apiKey, {
            ...this.defaultConfig,
            ...config,
        });
        StreamingWebSocketClient.getInstance();
    }

    /**
     * Initialize the streaming connection
     */
    public getClient(): Client {
        return StreamingWebSocketClient.getClient();
    }

    /**
     * Disconnect from the streaming service
     */
    public async disconnect(): Promise<void> {
        await StreamingWebSocketClient.disconnect();
    }

    /**
     * Check if the client is connected
     */
    public get isConnected(): boolean {
        return StreamingWebSocketClient.isConnected;
    }

    /**
     * Subscribe to a custom GraphQL subscription
     * This allows for advanced usage and future extensibility
     *
     * @param query - GraphQL subscription query
     * @param variables - Query variables
     * @param callbacks - Subscription callbacks
     * @returns Unsubscribe function
     */
    public rawQuery<T = Array<object>>(
        query: string,
        variables: Record<string, unknown>,
        callbacks: StreamSubscriptionOptions<T>
    ): UnsubscribeFunction {
        const client = StreamingWebSocketClient.getClient();

        return client.subscribe(
            {
                query,
                variables,
            },
            {
                next: (data) => callbacks.next(data as T),
                error: callbacks.error || (() => {}),
                complete: callbacks.complete || (() => {}),
            }
        );
    }

    /**
     *
     * The OHLCV Pairs stream provides real-time updates on the Open, High, Low, Close prices and Volume of one or many token pairs at configurable intervals.
     *
     * **Credit Cost**: 0 per call
     *
     * **Beta**: This endpoint is in beta.
     *
     * @param params - Parameters for the OHLCV pairs stream
     * @param callbacks - Subscription callbacks
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsubscribe = streamingService.subscribeToOHLCVPairs(
     *   {
     *     chain_name: StreamingChain.BASE_MAINNET,
     *     pair_addresses: ["0x9c087Eb773291e50CF6c6a90ef0F4500e349B903"],
     *     interval: StreamingInterval.ONE_MINUTE,
     *     timeframe: StreamingTimeframe.ONE_HOUR
     *   },
     *   {
     *     next: (data) => console.log("OHLCV Data:", data),
     *     error: (err) => console.error("Error:", err),
     *     complete: () => console.log("Stream completed")
     *   }
     * );
     * ```
     */
    public subscribeToOHLCVPairs(
        params: OHLCVPairsStreamParams,
        callbacks: StreamSubscriptionOptions<OHLCVPairsStreamResponse[]>
    ): UnsubscribeFunction {
        // Format the pair addresses array for the query
        const pairAddressesString = params.pair_addresses
            .map((addr) => `"${addr}"`)
            .join(", ");

        const query = `
      subscription {
        ohlcvCandlesForPair(
          chain_name: ${params.chain_name}
          pair_addresses: [${pairAddressesString}]
          interval: ${params.interval}
          timeframe: ${params.timeframe}
          ${params.limit ? `limit: ${params.limit}` : ""}
        ) {
          chain_name
          pair_address
          interval
          timeframe
          timestamp
          open
          high
          low
          close
          volume
          volume_usd
          quote_rate
          quote_rate_usd
          base_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
          quote_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return client.subscribe<{
            ohlcvCandlesForPair: OHLCVPairsStreamResponse[];
        }>(
            {
                query,
            },
            {
                next: (data) => {
                    if (data.data) {
                        const ohlcvData = data.data.ohlcvCandlesForPair;
                        if (ohlcvData) {
                            callbacks.next(ohlcvData);
                        }
                    } else if (data.errors) {
                        callbacks.error?.(data.errors);
                    }
                },
                error: callbacks.error || (() => {}),
                complete: callbacks.complete || (() => {}),
            }
        );
    }

    /**
     *
     * The OHLCV Tokens stream provides real-time updates on the Open, High, Low, Close prices and Volume of one or many tokens at configurable intervals.
     *
     * **Credit Cost**: 0 per call
     *
     * **Beta**: This endpoint is in beta.
     *
     * @param params - Parameters for the OHLCV tokens stream
     * @param callbacks - Subscription callbacks
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsubscribe = streamingService.subscribeToOHLCVTokens(
     *   {
     *     chain_name: StreamingChain.BASE_MAINNET,
     *     token_addresses: ["0x4B6104755AfB5Da4581B81C552DA3A25608c73B8"],
     *     interval: StreamingInterval.ONE_MINUTE,
     *     timeframe: StreamingTimeframe.ONE_HOUR
     *   },
     *   {
     *     next: (data) => console.log("OHLCV Token Data:", data),
     *     error: (err) => console.error("Error:", err),
     *     complete: () => console.log("Stream completed")
     *   }
     * );
     * ```
     */
    public subscribeToOHLCVTokens(
        params: OHLCVTokensStreamParams,
        callbacks: StreamSubscriptionOptions<OHLCVTokensStreamResponse[]>
    ): UnsubscribeFunction {
        // Format the token addresses array for the query
        const tokenAddressesString = params.token_addresses
            .map((addr) => `"${addr}"`)
            .join(", ");

        const query = `
      subscription {
        ohlcvCandlesForToken(
          chain_name: ${params.chain_name}
          token_addresses: [${tokenAddressesString}]
          interval: ${params.interval}
          timeframe: ${params.timeframe}
          ${params.limit ? `limit: ${params.limit}` : ""}
        ) {
          chain_name
          pair_address
          interval
          timeframe
          timestamp
          open
          high
          low
          close
          volume
          volume_usd
          quote_rate
          quote_rate_usd
          base_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
          quote_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return client.subscribe<{
            ohlcvCandlesForToken: OHLCVTokensStreamResponse[];
        }>(
            {
                query,
            },
            {
                next: (data) => {
                    if (data.data) {
                        const ohlcvData = data.data.ohlcvCandlesForToken;
                        if (ohlcvData) {
                            callbacks.next(ohlcvData);
                        }
                    } else if (data.errors) {
                        callbacks.error?.(data.errors);
                    }
                },
                error: callbacks.error || (() => {}),
                complete: callbacks.complete || (() => {}),
            }
        );
    }

    /**
     *
     * The New DEX Pairs stream provides real-time updates when new liquidity pairs are created on decentralized exchanges (DEXes). This documentation follows our standard streaming API structure.
     *
     * **Credit Cost**: 0 per call
     *
     * **Beta**: This endpoint is in beta.
     *
     * @param params - Parameters for the new pairs stream
     * @param callbacks - Subscription callbacks
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsubscribe = streamingService.subscribeToNewPairs(
     *   {
     *     chain_name: StreamingChain.BASE_MAINNET,
     *     protocols: [StreamingProtocol.UNISWAP_V2, StreamingProtocol.UNISWAP_V3]
     *   },
     *   {
     *     next: (data) => console.log("New Pairs:", data),
     *     error: (err) => console.error("Error:", err),
     *     complete: () => console.log("Stream completed")
     *   }
     * );
     * ```
     */
    public subscribeToNewPairs(
        params: NewPairsStreamParams,
        callbacks: StreamSubscriptionOptions<NewPairsStreamResponse[]>
    ): UnsubscribeFunction {
        const query = `
      subscription {
        newPairs(
          chain_name: ${params.chain_name},
          protocols: [${params.protocols.join(", ")}]
        ) {
          chain_name
          protocol
          protocol_version
          pair_address
          deployer_address
          tx_hash
          block_signed_at
          liquidity
          supply
          market_cap
          event_name
          quote_rate
          quote_rate_usd
          base_token {
            contract_address
            contract_decimals
            contract_name
            contract_ticker_symbol
          }
          pair {
            contract_address
            contract_decimals
            contract_name
            contract_ticker_symbol
          }
          quote_token {
            contract_address
            contract_decimals
            contract_name
            contract_ticker_symbol
          }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return client.subscribe<{
            newPairs: NewPairsStreamResponse[];
        }>(
            {
                query,
            },
            {
                next: (data) => {
                    if (data.data) {
                        const newPairsData = data.data.newPairs;
                        if (newPairsData) {
                            callbacks.next(newPairsData);
                        }
                    } else if (data.errors) {
                        callbacks.error?.(data.errors);
                    }
                },
                error: callbacks.error || (() => {}),
                complete: callbacks.complete || (() => {}),
            }
        );
    }

    /**
     *
     * The Update DEX Pairs stream provides real-time event updates on the prices, volumes, market cap and liquidity of one or many token pairs.
     *
     * **Credit Cost**: 0 per call
     *
     * **Beta**: This endpoint is in beta.
     *
     * @param params - Parameters for the update pairs stream
     * @param callbacks - Subscription callbacks
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsubscribe = streamingService.subscribeToUpdatePairs(
     *   {
     *     chain_name: StreamingChain.BASE_MAINNET,
     *     pair_addresses: [
     *       "0x3f0296BF652e19bca772EC3dF08b32732F93014A"
     *     ]
     *   },
     *   {
     *     next: (data) => console.log("Pair Updates:", data),
     *     error: (err) => console.error("Error:", err),
     *     complete: () => console.log("Stream completed")
     *   }
     * );
     * ```
     */
    public subscribeToUpdatePairs(
        params: UpdatePairsStreamParams,
        callbacks: StreamSubscriptionOptions<UpdatePairsStreamResponse>
    ): UnsubscribeFunction {
        const pairAddressesString = params.pair_addresses
            .map((addr) => `"${addr}"`)
            .join(", ");

        const timeframeFields = `
          volume { current_value previous_value pct_change }
          volume_usd { current_value previous_value pct_change }
          buy_volume { current_value previous_value pct_change }
          sell_volume { current_value previous_value pct_change }
          swap_count { current_value previous_value pct_change }
          buy_count { current_value previous_value pct_change }
          sell_count { current_value previous_value pct_change }
          unique_buyers { current_value previous_value pct_change }
          unique_sellers { current_value previous_value pct_change }
          price { current_value previous_value pct_change }
        `;

        const query = `
      subscription {
        updatePairs(
          chain_name: ${params.chain_name}
          pair_addresses: [${pairAddressesString}]
        ) {
          id
          chain_name
          pair_address
          timestamp
          quote_rate
          quote_rate_usd
          market_cap
          liquidity
          sender
          trader
          direction
          base_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
          quote_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
          last_5m { ${timeframeFields} }
          last_1hr { ${timeframeFields} }
          last_6hr { ${timeframeFields} }
          last_24hr { ${timeframeFields} }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return client.subscribe<{
            updatePairs: UpdatePairsStreamResponse;
        }>(
            {
                query,
            },
            {
                next: (data) => {
                    if (data.data) {
                        const updatePairsData = data.data.updatePairs;
                        if (updatePairsData) {
                            callbacks.next(updatePairsData);
                        }
                    } else if (data.errors) {
                        callbacks.error?.(data.errors);
                    }
                },
                error: callbacks.error || (() => {}),
                complete: callbacks.complete || (() => {}),
            }
        );
    }

    /**
     *
     * The Update Tokens stream provides real-time swap updates for the highest-volume pool of each specified token.
     *
     * **Credit Cost**: 0 per call
     *
     * **Beta**: This endpoint is in beta.
     *
     * @param params - Parameters for the update tokens stream
     * @param callbacks - Subscription callbacks
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsubscribe = streamingService.subscribeToUpdateTokens(
     *   {
     *     chain_name: StreamingChain.BASE_MAINNET,
     *     token_addresses: [
     *       "0x0b3e328455c4059EEb9e3f84b5543F74E24e7E1b"
     *     ]
     *   },
     *   {
     *     next: (data) => console.log("Token Updates:", data),
     *     error: (err) => console.error("Error:", err),
     *     complete: () => console.log("Stream completed")
     *   }
     * );
     * ```
     */
    public subscribeToUpdateTokens(
        params: UpdateTokensStreamParams,
        callbacks: StreamSubscriptionOptions<UpdateTokensStreamResponse>
    ): UnsubscribeFunction {
        const tokenAddressesString = params.token_addresses
            .map((addr) => `"${addr}"`)
            .join(", ");

        const timeframeFields = `
          volume { current_value previous_value pct_change }
          volume_usd { current_value previous_value pct_change }
          buy_volume { current_value previous_value pct_change }
          sell_volume { current_value previous_value pct_change }
          swap_count { current_value previous_value pct_change }
          buy_count { current_value previous_value pct_change }
          sell_count { current_value previous_value pct_change }
          unique_buyers { current_value previous_value pct_change }
          unique_sellers { current_value previous_value pct_change }
          price { current_value previous_value pct_change }
        `;

        const query = `
      subscription {
        updateTokens(
          chain_name: ${params.chain_name}
          token_addresses: [${tokenAddressesString}]
        ) {
          id
          chain_name
          pair_address
          timestamp
          quote_rate
          quote_rate_usd
          market_cap
          liquidity
          sender
          trader
          direction
          base_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
          quote_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
          last_5m { ${timeframeFields} }
          last_1hr { ${timeframeFields} }
          last_6hr { ${timeframeFields} }
          last_24hr { ${timeframeFields} }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return client.subscribe<{
            updateTokens: UpdateTokensStreamResponse;
        }>(
            {
                query,
            },
            {
                next: (data) => {
                    if (data.data) {
                        const updateTokensData = data.data.updateTokens;
                        if (updateTokensData) {
                            callbacks.next(updateTokensData);
                        }
                    } else if (data.errors) {
                        callbacks.error?.(data.errors);
                    }
                },
                error: callbacks.error || (() => {}),
                complete: callbacks.complete || (() => {}),
            }
        );
    }

    /**
     *
     * The Wallet Activity stream provides real-time updates on wallet transactions, token transfers, and interactions with smart contracts . This documentation follows our standard Streaming API structure.
     *
     * **Credit Cost**: 0 per call
     *
     * **Beta**: This endpoint is in beta.
     *
     * @param params - Parameters for the wallet activity stream
     * @param callbacks - Subscription callbacks
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsubscribe = streamingService.subscribeToWalletActivity(
     *   {
     *     chain_name: StreamingChain.BASE_MAINNET,
     *     wallet_addresses: ["0x198ef79f1f515f02dfe9e3115ed9fc07183f02fc"]
     *   },
     *   {
     *     next: (data) => console.log("Wallet Activity:", data),
     *     error: (err) => console.error("Error:", err),
     *     complete: () => console.log("Stream completed")
     *   }
     * );
     * ```
     */
    public subscribeToWalletActivity(
        params: WalletActivityStreamParams,
        callbacks: StreamSubscriptionOptions<WalletActivityStreamResponse[]>
    ): UnsubscribeFunction {
        // Format the wallet addresses array for the query
        const walletAddressesString = params.wallet_addresses
            .map((addr) => `"${addr}"`)
            .join(", ");

        const query = `
      subscription {
        walletTxs(
          chain_name: ${params.chain_name},
          wallet_addresses: [${walletAddressesString}]
        ) {
          tx_hash
          from_address
          to_address
          value
          chain_name
          block_signed_at
          block_height
          block_hash
          miner_address
          gas_used
          tx_offset
          successful
          decoded_type
          decoded_details {
            ... on TransferTransaction {
              from
              to
              amount
              quote_usd
              quote_rate_usd
              contract_metadata {
                contract_name
                contract_address
                contract_decimals
                contract_ticker_symbol
              }
            }
            ... on SwapTransaction {
              token_in {
                contract_name
                contract_address
                contract_decimals
                contract_ticker_symbol
              }
              token_out {
                contract_name
                contract_address
                contract_decimals
                contract_ticker_symbol
              }
              amount_in
              amount_out
            }
            ... on BridgeTransaction {
              type
              typeString
              from
              to
              amount
              quote_usd
              quote_rate_usd
              contract_metadata {
                contract_name
                contract_address
                contract_decimals
                contract_ticker_symbol
              }
            }
            ... on DepositTransaction {
              from
              to
              amount
              quote_usd
              quote_rate_usd
              contract_metadata {
                contract_name
                contract_address
                contract_decimals
                contract_ticker_symbol
              }
            }
            ... on WithdrawTransaction {
              from
              to
              amount
              quote_usd
              quote_rate_usd
              contract_metadata {
                contract_name
                contract_address
                contract_decimals
                contract_ticker_symbol
              }
            }
            ... on ApproveTransaction {
              spender
              amount
              quote_usd
              quote_rate_usd
              contract_metadata {
                contract_name
                contract_address
                contract_decimals
                contract_ticker_symbol
              }
            }
            ... on ErrorDetails {
              message
            }
          }
          logs {
            emitter_address
            log_offset
            data
            topics
          }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return client.subscribe<{
            walletTxs: WalletActivityStreamResponse[];
        }>(
            {
                query,
            },
            {
                next: (data) => {
                    if (data.data) {
                        const walletActivityData = data.data.walletTxs;
                        if (walletActivityData) {
                            callbacks.next(walletActivityData);
                        }
                    } else if (data.errors) {
                        callbacks.error?.(data.errors);
                    }
                },
                error: callbacks.error || (() => {}),
                complete: callbacks.complete || (() => {}),
            }
        );
    }

    /**
     * Search for tokens by keyword, ticker, or contract address
     *
     * @param params - Parameters for the token search query
     * @returns Promise resolving to an array of matching tokens
     *
     * @example
     * ```typescript
     * const results = await streamingService.searchToken({
     *   query: "PEPE"
     * });
     * console.log("Search Results:", results);
     * ```
     */
    public searchToken(
        params: TokenSearchParams
    ): Promise<TokenSearchResponse[]> {
        const query = `
      query SearchToken($query: String!, $chain_name: ChainName) {
        searchToken(query: $query, chain_name: $chain_name) {
          pair_address
          chain_name
          quote_rate
          quote_rate_usd
          volume
          volume_usd
          market_cap
          base_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
          quote_token {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return new Promise((resolve, reject) => {
            let result: TokenSearchResponse[] = [];
            client.subscribe<{
                searchToken: TokenSearchResponse[];
            }>(
                {
                    query,
                    variables: {
                        query: params.query,
                        chain_name: params.chain_name,
                    },
                },
                {
                    next: (data) => {
                        if (data.data) {
                            result = data.data.searchToken ?? [];
                        } else if (data.errors) {
                            reject(data.errors);
                        }
                    },
                    error: (err) => reject(err),
                    complete: () => resolve(result),
                }
            );
        });
    }

    /**
     * Get top trader wallets and their PnL for a specific token
     *
     * @param params - Parameters for the UPnL token query
     * @returns Promise resolving to an array of top trader wallet data
     *
     * @example
     * ```typescript
     * const traders = await streamingService.getUPnLForToken({
     *   chain_name: StreamingChain.ETH_MAINNET,
     *   token_address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48"
     * });
     * console.log("Top Traders:", traders);
     * ```
     */
    public getUPnLForToken(
        params: UPnLForTokenParams
    ): Promise<UPnLForTokenResponse[]> {
        const query = `
      query {
        upnlForToken(
          chain_name: ${params.chain_name}
          token_address: "${params.token_address}"
        ) {
          token_address
          wallet_address
          volume
          transactions_count
          pnl_realized_usd
          balance
          balance_pretty
          pnl_unrealized_usd
          contract_metadata {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return new Promise((resolve, reject) => {
            let result: UPnLForTokenResponse[] = [];
            client.subscribe<{
                upnlForToken: UPnLForTokenResponse[];
            }>(
                {
                    query,
                },
                {
                    next: (data) => {
                        if (data.data) {
                            result = data.data.upnlForToken ?? [];
                        } else if (data.errors) {
                            reject(data.errors);
                        }
                    },
                    error: (err) => reject(err),
                    complete: () => resolve(result),
                }
            );
        });
    }

    /**
     * Get unrealized and realized PnL for all tokens held by a wallet
     *
     * @param params - Parameters for the UPnL wallet query
     * @returns Promise resolving to an array of token PnL data for the wallet
     *
     * @example
     * ```typescript
     * const pnl = await streamingService.getUPnLForWallet({
     *   chain_name: StreamingChain.ETH_MAINNET,
     *   wallet_address: "0xfe97b0C517a84F98fc6eDe3CD26B43012d31992a"
     * });
     * console.log("Wallet PnL:", pnl);
     * ```
     */
    public getUPnLForWallet(
        params: UPnLForWalletParams
    ): Promise<UPnLForWalletResponse[]> {
        const query = `
      query {
        upnlForWallet(
          chain_name: ${params.chain_name}
          wallet_address: "${params.wallet_address}"
        ) {
          token_address
          cost_basis
          current_price
          pnl_realized_usd
          pnl_unrealized_usd
          net_balance_change
          marketcap_usd
          contract_metadata {
            contract_name
            contract_address
            contract_decimals
            contract_ticker_symbol
          }
        }
      }
    `;

        const client = StreamingWebSocketClient.getClient();

        return new Promise((resolve, reject) => {
            let result: UPnLForWalletResponse[] = [];
            client.subscribe<{
                upnlForWallet: UPnLForWalletResponse[];
            }>(
                {
                    query,
                },
                {
                    next: (data) => {
                        if (data.data) {
                            result = data.data.upnlForWallet ?? [];
                        } else if (data.errors) {
                            reject(data.errors);
                        }
                    },
                    error: (err) => reject(err),
                    complete: () => resolve(result),
                }
            );
        });
    }
}

/**
 * Singleton WebSocket Client for GoldRush Streaming
 *
 */
class StreamingWebSocketClient {
    private static apiKey: string;
    private static instance: StreamingWebSocketClient | null = null;
    private static client: Client | null = null;
    private static connected = false;
    private static reconnectAttempts = 0;
    private static config: Required<StreamingServiceConfig>;

    private constructor() {}

    public static getInstance(): StreamingWebSocketClient {
        if (!StreamingWebSocketClient.instance) {
            StreamingWebSocketClient.instance = new StreamingWebSocketClient();
        }
        return StreamingWebSocketClient.instance;
    }

    public static configure(
        apiKey: string,
        config: Required<StreamingServiceConfig>
    ): void {
        StreamingWebSocketClient.apiKey = apiKey;
        StreamingWebSocketClient.config = config;
    }

    public static getClient(): Client {
        if (
            !StreamingWebSocketClient.client ||
            !StreamingWebSocketClient.connected
        ) {
            StreamingWebSocketClient.connect();
        }
        return StreamingWebSocketClient.client!;
    }

    private static connect(): void {
        if (StreamingWebSocketClient.client) return;

        StreamingWebSocketClient.client = createClient({
            url: "wss://streaming.goldrushdata.com/graphql",
            lazyCloseTimeout: 3000,
            connectionParams: {
                GOLDRUSH_API_KEY: StreamingWebSocketClient.apiKey,
            },
            shouldRetry: () =>
                StreamingWebSocketClient.config.shouldRetry(
                    StreamingWebSocketClient.reconnectAttempts
                ),
            on: {
                connecting: () => {
                    StreamingWebSocketClient.config.onConnecting();
                },
                opened: () => {
                    StreamingWebSocketClient.config.onOpened();
                    StreamingWebSocketClient.reconnectAttempts = 0;
                    StreamingWebSocketClient.connected = true;
                },
                closed: () => {
                    StreamingWebSocketClient.config.onClosed();
                    StreamingWebSocketClient.connected = false;
                },
                error: (err) => {
                    StreamingWebSocketClient.config.onError(err);
                    StreamingWebSocketClient.connected = false;
                },
            },
        });
    }

    public static get isConnected(): boolean {
        return StreamingWebSocketClient.connected;
    }

    public static async disconnect(): Promise<void> {
        if (!StreamingWebSocketClient.client) return;
        if (!StreamingWebSocketClient.connected) return;

        // * INFO: Is a fire & forget
        await StreamingWebSocketClient.client.dispose();
        StreamingWebSocketClient.client = null;
        StreamingWebSocketClient.connected = false;
        // * INFO: Wait for the client to disconnect
        await new Promise((resolve) => setTimeout(resolve, 1000));
    }
}
